/**
 * onboarding/survey SDK 플러그인이 공유하는 옵저버/식별 로직.
 *
 * 두 `.client.ts` 플러그인(`6.onboardingSdk`, `7.surveySdk`)은 role/locale/팝업 상태에 맞춰
 * SDK observer 를 재구성하는 동일한 패턴(seq 레이스 가드 + 직전 핸들 destroy + 팝업 시 스킵)을
 * 중복으로 갖고 있었다. 클로저에 갇혀 단위 테스트가 불가능했던 이 로직을 순수 함수/팩토리로 추출한다.
 */

/** observe() 가 돌려주는 핸들 중 플러그인이 의존하는 최소 형태(온보딩/설문 핸들의 교집합). */
export interface SdkObserveHandle {
    setLocale: (locale: string) => void;
    destroy: () => void;
}

/**
 * `$i18n.locale`(문자열 또는 ref 형태)를 SDK 에 넘길 소문자 언어코드로 정규화한다.
 * 빈 값이면 `'en'` 으로 폴백한다.
 */
export const resolveSdkLanguage = (
    locale: string | { value: string } | undefined | null
): string => {
    const raw = typeof locale === 'string' ? locale : locale?.value ?? '';
    return raw.toLowerCase() || 'en';
};

/**
 * SDK observe 에 넘길 role. 사용자 데이터(uuid)가 로드되기 전에는 `undefined` 로 둬서
 * role 무관 entry 만 매칭되게 한다. uuid 가 있으면 관리자 여부로 ADMIN/USER 구분.
 */
export const resolveSdkRole = (user: {
    uuid?: string;
    isAdmin: boolean;
}): 'ADMIN' | 'USER' | undefined => {
    if (!user.uuid) return undefined;
    return user.isAdmin ? 'ADMIN' : 'USER';
};

/** 다이얼로그(단일 GenericAlert 또는 멀티 다이얼로그)가 하나라도 열려 있으면 true. */
export const isAnyPopupOpen = (dialog: {
    show: boolean;
    multiOption: unknown[];
}): boolean => dialog.show || dialog.multiOption.length > 0;

export interface SdkReconcilerDeps<H extends SdkObserveHandle> {
    /** 현재 role/locale/도메인으로 SDK observe 를 호출하는 thunk. */
    observe: () => Promise<H | null>;
    /** 팝업 노출 중이면 observe 를 스킵(런처/투어 숨김). */
    isPopupOpen: () => boolean;
    /** observe 실패 시 호출(로깅용). 미지정 시 조용히 무시. */
    onError?: (error: unknown) => void;
}

export interface SdkReconciler<H extends SdkObserveHandle> {
    reconcile: () => Promise<void>;
    getHandle: () => H | null;
    /** 현재 핸들이 있으면 locale 갱신(없으면 no-op). */
    setLocale: (locale: string) => void;
}

/**
 * SDK observer 재구성기. SDK 가 setRole 을 지원하지 않으므로 destroy 후 observe 를 재호출하는 패턴.
 *
 * - seq 레이스 가드: `await observe()` 사이 더 최신 reconcile 이 실행되면, 늦게 도착한 핸들은
 *   폐기(destroy)하고 보관하지 않아 stale observer 가 잔존하지 않게 한다.
 * - 팝업이 열려 있으면 직전 핸들을 destroy 하고 observe 를 스킵한다.
 */
export const createSdkReconciler = <H extends SdkObserveHandle>(
    deps: SdkReconcilerDeps<H>
): SdkReconciler<H> => {
    let handle: H | null = null;
    let seq = 0;

    const reconcile = async (): Promise<void> => {
        const mySeq = ++seq;
        handle?.destroy();
        handle = null;

        if (deps.isPopupOpen()) return; // 팝업 노출 중 → 런처/투어 숨김

        try {
            const next = await deps.observe();
            if (mySeq !== seq) {
                // await 사이 더 최신 reconcile 이 실행됨 → 이 핸들은 폐기
                next?.destroy();
                return;
            }
            handle = next;
        } catch (error) {
            deps.onError?.(error);
        }
    };

    return {
        reconcile,
        getHandle: () => handle,
        setLocale: (locale: string) => handle?.setLocale(locale),
    };
};
